import { Fragment, useMemo } from "react"
import { Link } from "@tanstack/react-router"

import type { ProjectStatusTypes } from "@productlane/ui"
import { cn } from "@productlane/lib/src/browser"
import { formatRevenue } from "@productlane/lib/src/format-revenue"
import { Empty, ProjectStatusIcon, Tooltip } from "@productlane/ui"

import type { ReportingQueryProps } from "@/screens/app/reporting/reporting-query"
import EmptyProject from "@/assets/empty-states/empty-project.png"
import { EntityFeedbackChart } from "@/components/entity-feedback-chart"
import { ReportingSectionHeader } from "@/components/reporting-section-header"
import { api } from "@/lib/api"
import { useDetailLinkFactory } from "@/lib/detail-drawer"
import { getProjectIcon } from "@/lib/projects"
import { useZeroWorkspace } from "@/lib/zero"

type ProjectsVisualizationProps = ReportingQueryProps

const projectStateOrder = ["started", "planned", "paused", "backlog"] as const
const projectStateNames: Record<string, string> = {
	started: "In Progress",
	planned: "Planned",
	paused: "Paused",
	backlog: "Backlog",
}

export const ProjectsVisualization = ({
	startDate,
	endDate,
	...filters
}: ProjectsVisualizationProps) => {
	const workspace = useZeroWorkspace()
	const detailLink = useDetailLinkFactory()

	const projectsMetricsQuery = api.workspace.getProjectsMetrics.useQuery(
		{ startDate, endDate, ...filters },
		{
			cacheTime: Infinity,
			staleTime: Infinity,
		},
	)

	const { groupedProjects } = useMemo(() => {
		const projects = (projectsMetricsQuery.data?.projects || []).filter(
			(project) =>
				project.feedbackDates.length > 0 &&
				project.state !== "completed" &&
				project.state !== "canceled" &&
				project.state !== "closed",
		)
		const grouped = new Map<string, typeof projects>()

		for (const project of projects) {
			const state = project.state || "backlog"
			if (!grouped.has(state)) {
				grouped.set(state, [])
			}
			grouped.get(state)!.push(project)
		}

		for (const [, projectList] of grouped) {
			projectList.sort((a, b) => b.painLevel - a.painLevel)
		}

		return { groupedProjects: grouped }
	}, [projectsMetricsQuery.data])

	const orderedStates = projectStateOrder.filter((state) =>
		groupedProjects.has(state),
	)

	const hasProjects = orderedStates.length > 0 || projectsMetricsQuery.isLoading

	return (
		<>
			{hasProjects ? (
				<div>
					{orderedStates.map((state) => {
						const projects = groupedProjects.get(state) || []
						const totalPainLevel = projects.reduce(
							(sum, p) => sum + p.painLevel,
							0,
						)
						const maxPainInGroup = Math.max(
							...projects.map((p) => p.painLevel),
							0,
						)

						return (
							<Fragment key={state}>
								<ReportingSectionHeader
									icon={
										<ProjectStatusIcon
											status={state as ProjectStatusTypes}
											className="h-3.5 w-3.5"
											colored
										/>
									}
									title={projectStateNames[state] || state}
									count={totalPainLevel}
								/>
								<div>
									{projects.map((project) => {
										const Icon = getProjectIcon(project.icon)
										const relativePainLevel =
											maxPainInGroup > 0
												? project.painLevel / maxPainInGroup
												: 0
										const painLevelInPx = Math.round(relativePainLevel * 40)

										return (
											<Link
												key={project.id}
												className={cn(
													"flex items-center gap-[1.2rem] px-5 py-2 text-sm hover:bg-gray-50 sm:px-6 dark:hover:bg-gray-850",
												)}
												{...detailLink("project", project.id)}
											>
												<div className="flex min-w-[6rem] flex-1 items-center gap-3 truncate">
													<Icon
														className="h-4 w-4 flex-none"
														style={{ color: project.color ?? "" }}
													/>
													<span className="truncate font-medium">
														{project.name}
													</span>
												</div>

												<div className="w-16 flex-none md:w-32">
													<EntityFeedbackChart
														feedbackDates={project.feedbackDates}
														color={project.color ?? "#6b7280"}
														startDate={startDate}
														endDate={endDate}
													/>
												</div>

												<div className="ml-2 flex w-auto flex-none items-center gap-2 text-gray-700 md:ml-6 md:w-20 dark:text-gray-300">
													<div className="relative hidden h-1 w-10 rounded bg-gray-200 md:block dark:bg-gray-800">
														<div
															className="absolute h-1 rounded"
															style={{
																width: `${painLevelInPx}px`,
																backgroundColor: project.color ?? "#6b7280",
															}}
														/>
													</div>

													<p className="w-7">{project.painLevel}</p>
												</div>

												{workspace && (
													<Tooltip label="Aggregated Revenue">
														<div className="ml-2 flex w-10 flex-none items-center text-gray-700 dark:text-gray-300">
															<p className="text-sm">
																{formatRevenue({
																	revenue: project.aggregatedRevenue,
																	revenueFormatting:
																		workspace.revenueFormatting,
																	currency: workspace.revenueCurrency,
																})}
															</p>
														</div>
													</Tooltip>
												)}
											</Link>
										)
									})}
								</div>
							</Fragment>
						)
					})}
				</div>
			) : (
				<div className="flex justify-center py-12">
					<Empty
						title="Projects"
						text="See which projects are getting the most regular customer requests over time."
						className="max-w-[20rem]"
						image={
							<img
								width={130}
								height={128}
								src={EmptyProject}
								alt="Empty projects"
							/>
						}
					/>
				</div>
			)}
		</>
	)
}
